import { DialogHelper } from './dialog';
import { historyAwareGuestActions } from './history';
import { showNotification } from './notifications';
import { $customFields, $guests, $occupiedSeats } from './state';
import type { Guest } from './types';

import { isDuplicateName, validateSeatPair, validateSeatRange, parseIntOrNull, isSeatOccupied } from './domain/validation';
import { getMaxSeatCount, getMaxTableNumber } from './constants';

const dialog = new DialogHelper();

// Unified guest dialog functionality for both add and edit
export function showGuestDialog(guest?: Guest): void {
  const isEditing = !!guest;
  
  // Clone the edit guest template (used for both add and edit)
  const template = document.getElementById('edit-guest-template') as HTMLTemplateElement;
  const fragment = template.content.cloneNode(true) as DocumentFragment;
  
  // Create a container element for the dialog content
  const dialogContent = document.createElement('div');
  dialogContent.appendChild(fragment);
  
  // Get form inputs
  const nameInput = dialogContent.querySelector('#guestNameInput') as HTMLInputElement;
  const tableInput = dialogContent.querySelector('#tableInput') as HTMLInputElement;
  const seatInput = dialogContent.querySelector('#seatInput') as HTMLInputElement;
  const customFieldInputsContainer = dialogContent.querySelector('#customFieldInputs') as HTMLDivElement;
  const maxTable = getMaxTableNumber();
  const maxSeat = getMaxSeatCount();

  tableInput.max = maxTable.toString();
  tableInput.placeholder = `1-${maxTable}`;
  seatInput.max = maxSeat.toString();
  seatInput.placeholder = `1-${maxSeat}`;
  
  // Create custom field inputs if custom fields exist
  const customFieldConfigs = $customFields.get();
  if (customFieldConfigs.length > 0) {
    customFieldInputsContainer.style.display = 'block';
    
    customFieldConfigs.forEach(config => {
      const fieldDiv = document.createElement('div');
      fieldDiv.className = 'form-field';
      
      const label = document.createElement('label');
      label.className = 'form-label';
      label.textContent = config.displayName;
      label.setAttribute('for', `customField-${config.key}`);
      
      const input = document.createElement('input');
      input.type = 'text';
      input.id = `customField-${config.key}`;
      input.className = 'form-input';
      input.placeholder = `Enter ${config.displayName.toLowerCase()}`;
      input.dataset.fieldKey = config.key;
      
      fieldDiv.appendChild(label);
      fieldDiv.appendChild(input);
      customFieldInputsContainer.appendChild(fieldDiv);
    });
  }
  
  // Populate form if editing existing guest
  if (isEditing && guest) {
    nameInput.value = guest.fullName;
    tableInput.value = guest.table ? guest.table.toString() : '';
    seatInput.value = guest.seat ? guest.seat.toString() : '';
    
    // Populate custom field values
    customFieldConfigs.forEach(config => {
      const input = dialogContent.querySelector(`#customField-${config.key}`) as HTMLInputElement;
      if (input && guest[config.key]) {
        input.value = guest[config.key] || '';
      }
    });
  }

  const tipDiv = document.createElement('div');
  tipDiv.style.cssText = 'font-size: 13px; color: #6b7280; background: #f9fafb; padding: 12px; border-radius: 6px; border: 1px solid #e5e7eb; margin-top: 16px;';
  tipDiv.innerHTML = '<strong>Tip:</strong> Leave table and seat empty to add as an unplaced guest, or fill both to seat immediately.';
  dialogContent.appendChild(tipDiv);

  // Show dialog
  dialog.show({
    title: isEditing ? 'Edit Guest' : 'Add New Guest',
    content: dialogContent,
    className: isEditing ? 'edit-guest-dialog' : 'add-guest-dialog',
    showCloseButton: true
  }, [
    {
      text: 'Cancel',
      handler: () => dialog.close()
    },
    {
      text: isEditing ? 'Save Changes' : 'Add Guest',
      primary: true,
      handler: () => handleGuestSubmit(guest)
    }
  ]);

  // Set up keyboard accessibility after dialog is shown
  setTimeout(() => {
    const nameInput = document.getElementById('guestNameInput') as HTMLInputElement;
    const tableInput = document.getElementById('tableInput') as HTMLInputElement;
    const seatInput = document.getElementById('seatInput') as HTMLInputElement;
    
    if (nameInput) {
      nameInput.focus();
      if (isEditing) {
        nameInput.select();
      }
      
      nameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && tableInput) {
          e.preventDefault();
          tableInput.focus();
        }
      });
    }
    
    if (tableInput) {
      tableInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && seatInput) {
          e.preventDefault();
          seatInput.focus();
        }
      });
    }
    
    if (seatInput) {
      seatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleGuestSubmit(guest);
        }
      });
    }
  }, 100);
}

function handleGuestSubmit(guest?: Guest): void {
  const isEditing = !!guest;
  const validation = validateGuestForm(guest);
  
  if (!validation.isValid) {
    return;
  }
  
  try {
    if (isEditing && guest) {
      // Update existing guest with dynamic custom fields
      const updateData: any = {
        fullName: validation.name,
        table: validation.table,
        seat: validation.seat,
      };
      
      // Add all custom fields to update data
      Object.keys(validation.customFields).forEach(key => {
        updateData[key] = validation.customFields[key];
      });
      
      historyAwareGuestActions.updateGuest(guest.id, updateData);
      
      const seatingInfo = validation.table && validation.seat 
        ? ` at Table ${validation.table}, Seat ${validation.seat}` 
        : ' (unplaced)';
      showNotification(
        'Guest Updated', 
        `${validation.name} has been updated${seatingInfo}`,
        'success'
      );
    } else {
      // Create new guest with dynamic custom fields
      const newGuest = historyAwareGuestActions.createGuest(
        validation.name, 
        validation.table || undefined, 
        validation.seat || undefined,
        validation.customFields.customField1,
        validation.customFields.customField2
      );
      
      const message = validation.table && validation.seat 
        ? `${newGuest.fullName} has been added and seated at Table ${validation.table}, Seat ${validation.seat}`
        : `${newGuest.fullName} has been added to the unplaced guests list`;
      
      showNotification(
        'Guest Added', 
        message,
        'success'
      );
    }
    
    dialog.close();
  } catch (error) {
    showGuestError(`Failed to ${isEditing ? 'update' : 'add'} guest. Please try again.`);
    console.error('Guest operation error:', error);
  }
}

function validateGuestForm(guest?: Guest): {
  isValid: boolean;
  name: string;
  table: number | null;
  seat: number | null;
  customFields: { [key: string]: string };
} {
  const nameInput = document.getElementById('guestNameInput') as HTMLInputElement;
  const tableInput = document.getElementById('tableInput') as HTMLInputElement;
  const seatInput = document.getElementById('seatInput') as HTMLInputElement;

  showGuestError('');

  const newName = nameInput.value.trim();
  if (!newName) {
    showGuestError('Guest name is required');
    nameInput.focus();
    return { isValid: false, name: '', table: null, seat: null, customFields: {} };
  }

  const allGuests = $guests.get();
  if (isDuplicateName(allGuests, newName, guest?.id)) {
    showGuestError('A guest with this name already exists');
    nameInput.focus();
    return { isValid: false, name: newName, table: null, seat: null, customFields: {} };
    }

  const tableNum = parseIntOrNull(tableInput.value);
  const seatNum  = parseIntOrNull(seatInput.value);

  const pairErr = validateSeatPair(tableNum, seatNum);
  if (pairErr) {
    showGuestError(pairErr);
    (tableNum === null ? tableInput : seatInput).focus();
    return { isValid: false, name: newName, table: tableNum, seat: seatNum, customFields: {} };
  }

  if (tableNum !== null && seatNum !== null) {
    const rangeErr = validateSeatRange(tableNum, seatNum);
    if (rangeErr) {
      showGuestError(rangeErr);
      (rangeErr.includes('Table') ? tableInput : seatInput).focus();
      return { isValid: false, name: newName, table: tableNum, seat: seatNum, customFields: {} };
    }

    const occupiedSeats = $occupiedSeats.get();
    const currentSeatKey = guest?.table && guest?.seat ? `${guest.table}-${guest.seat}` : null;
    if (isSeatOccupied(occupiedSeats, tableNum, seatNum, currentSeatKey)) {
      showGuestError(`Table ${tableNum}, Seat ${seatNum} is already occupied`);
      tableInput.focus();
      return { isValid: false, name: newName, table: tableNum, seat: seatNum, customFields: {} };
    }
  }

  // collect custom fields
  const customFields: { [key: string]: string } = {};
  const customFieldConfigs = $customFields.get();
  customFieldConfigs.forEach(config => {
    const input = document.getElementById(`customField-${config.key}`) as HTMLInputElement;
    const v = input?.value?.trim();
    if (v) customFields[config.key] = v;
  });

  return { isValid: true, name: newName, table: tableNum, seat: seatNum, customFields };
}

function showGuestError(message: string): void {
  const errorDiv = document.getElementById('editError') as HTMLDivElement;
  if (errorDiv) {
    if (message) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
    } else {
      errorDiv.style.display = 'none';
      errorDiv.textContent = '';
    }
  }
}
